import datapoints from './datapoints';
import showNotificationOnSchedule from './helpers/showNotificationOnSchedule';
import scheduleConversationsService from '../services/scheduleConversationsService';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { OwaDate } from 'owa-datetime';
import { actionNames, DeleteConversationStoreUpdate } from 'owa-mail-store-actions';
import { processApplyConversationActionResponseMessage } from 'owa-mail-triage-action-response-processor';
import * as undoActions from 'owa-mail-undo';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import {
    getRowIdsFromRowKeys,
    filterConversationRowsByRestriction,
    getApplyConversationActionContext,
    TableView,
} from 'owa-mail-list-store';

/**
 * Schedule or Unschedule conversations
 * @param conversationRowKeys the row keys
 * @param tableView the tableView
 * @param actionSouce the actionSource
 * @param scheduleReturnTime the time conversation will return or undefined to unschedule
 */
export default createLegacyActionWithDatapoint('ScheduleConversation', datapoints.scheduleItems)(
    function scheduleConversations(
        conversationRowKeys: string[],
        tableView: TableView,
        actionSource: ActionSource,
        scheduleReturnTime: OwaDate | undefined
    ): Promise<void> {
        const tableViewId = tableView.id;
        const filteredRowKeys = filterConversationRowsByRestriction(
            conversationRowKeys,
            tableViewId,
            tableConversationRelation => !!tableConversationRelation
        );

        if (filteredRowKeys.length == 0) {
            return Promise.resolve();
        }

        const conversationIdStrings = getRowIdsFromRowKeys(filteredRowKeys, tableViewId);
        const conversationActionContexts = filteredRowKeys.map(rowKey =>
            getApplyConversationActionContext(rowKey, tableViewId)
        );

        const folderId = tableView.tableQuery.folderId;
        const scheduledFolderId = folderNameToId('scheduled');
        const isScheduledFolder = folderId === scheduledFolderId;

        undoActions.clearLastUndoableAction();
        showNotificationOnSchedule(!isScheduledFolder, scheduleReturnTime);

        // 1. Make local update when not in scheduled folder
        if (!isScheduledFolder) {
            raiseAction<DeleteConversationStoreUpdate>(actionNames.DeleteConversationStoreUpdate)(
                conversationIdStrings,
                tableView
            );
        }

        // 2. Make service request
        // When scheduling conversation we should act upon folderId and not on serverFolderId
        // so that entire conversation and not just the items belonging to the serverFolder get scheduled
        const scheduleConversationsPromise = scheduleConversationsService(
            conversationActionContexts,
            folderId /* contextFolderId */,
            scheduleReturnTime
        );

        // Do not allow undo within scheduled as we have no way to recover ReturnTime
        if (!isScheduledFolder) {
            undoActions.addActionToUndoStack(async () => {
                const response = await scheduleConversationsPromise;
                if (response.ResponseClass === 'Success') {
                    // Perform unschedule
                    await scheduleConversationsService(
                        conversationActionContexts,
                        scheduledFolderId /* contextFolderId */
                    );
                }
                return Promise.resolve();
            }, tableView.serverFolderId);
        }

        return processApplyConversationActionResponseMessage(
            scheduleConversationsPromise,
            filteredRowKeys,
            tableView.serverFolderId,
            tableView,
            false /* shouldRemoveRowsFromTombstone */
        );
    }
);
